import { SyntheticEvent } from "react";
import { LatLngTuple } from "leaflet";
import imageCompression from 'browser-image-compression';
// External Imports ----------------------------------------------------
import axios, { ResponseType, GenericAbortSignal } from "axios";
import heic2any from "heic2any";
//Internal Imports -----------------------------------------------------
import { useAuthStore } from "src/components/main/authStore";
import { json, simple } from "./types";
import { useModalStore } from "../main/modalStore";
import { redirect } from "react-router-dom";
// Constants -----------------------------------------------------
export const appTitle = "Material Testing and Inspection";
export const isDevMode = process.env.NODE_ENV === "development";
export const universityProjectHashKey = "95325bbb02fe33ec16f2d1c272b6f09c";
export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? "https://api.constructsmarter.com/mti/").replace(/\/?$/, "/");

export const defaultEmptyDate = "0000-00-00";
export const defaultEmptyDateTime = "0000-00-00 00:00:00";
export const defaultEmptyTime = "00:00:00";
export const defaultEmptyPMNumber = "00000000";
export const testerEmails = [
    "jun909l@yahoo.com"
];
export const weekDays = [
    "Monday", 
    "Tuesday", 
    "Wednesday", 
    "Thursday", 
    "Friday", 
    "Saturday", 
    "Sunday", 
];
export const month = [
    "January", 
    "February", 
    "March", 
    "April", 
    "May", 
    "June", 
    "July", 
    "August", 
    "September", 
    "October", 
    "November", 
    "December"
];
export const mon = [
    "Jan", 
    "Feb", 
    "Mar", 
    "Apr", 
    "May", 
    "Jun", 
    "Jul",
    "Aug", 
    "Sep", 
    "Oct", 
    "Nov", 
    "Dec"
];
interface mimeTypeMapType {
    "txt": "text/plain", 
    "xml": "text/xml",
    "kml": "application/vnd.google-earth.kml+xml", 
    "pdf": "application/pdf", 
    "xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", 
    "xlsm": "application/vnd.ms-excel.sheet.macroenabled.12", 
    "xls": "application/vnd.ms-excel", 
    "csv": "text/csv", 
    "ppt": "application/vnd.ms-powerpoint",
    "pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation", 
    "docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document", 
    "xps": "application/vnd.ms-xpsdocument", 
    "docm": "application/vnd.ms-word.document.macroenabled.12", 
    "doc": "application/msword", 
    "accdb": "application/msaccess", 
    "msg": "application/vnd.ms-outlook", 
    "rtf": "application/rtf", 
    "png": "image/png", 
    "jpg": "image/jpeg", 
    "jpeg": "image/jpeg", 
    "svg": "image/svg+xml", 
    "gif": "image/gif", 
    "bmp": "image/bmp", 
    "mp3": "audio/mpeg", 
    "mp4": "video/mp4", 
    "zip": "application/x-zip-compressed", 
    "json": "application/json",
    "folder": "application/vnd.google-apps.folder",
    "document": "application/vnd.google-apps.document"
}
export const mimeTypeMap: mimeTypeMapType = {
    "txt": "text/plain", 
    "xml": "text/xml",
    "kml": "application/vnd.google-earth.kml+xml", 
    "pdf": "application/pdf", 
    "xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", 
    "xlsm": "application/vnd.ms-excel.sheet.macroenabled.12", 
    "xls": "application/vnd.ms-excel", 
    "csv": "text/csv", 
    "ppt": "application/vnd.ms-powerpoint",
    "pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation", 
    "docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document", 
    "xps": "application/vnd.ms-xpsdocument", 
    "docm": "application/vnd.ms-word.document.macroenabled.12", 
    "doc": "application/msword", 
    "accdb": "application/msaccess", 
    "msg": "application/vnd.ms-outlook", 
    "rtf": "application/rtf", 
    "png": "image/png", 
    "jpg": "image/jpeg", 
    "jpeg": "image/jpeg", 
    "svg": "image/svg+xml", 
    "gif": "image/gif", 
    "bmp": "image/bmp", 
    "mp3": "audio/mpeg", 
    "mp4": "video/mp4", 
    "zip": "application/x-zip-compressed", 
    "json": "application/json",
    "folder": "application/vnd.google-apps.folder",
    "document": "application/vnd.google-apps.document"
};
export const mimeTypeMapReverse  = Object.fromEntries(Object.entries(mimeTypeMap).map(([key, value]) => [value, key]));
export const outsideRoleCode = {
    "locator": "CURRENT PG&E Locate & Mark/USAs", 
    "standby": "Gas Standby", 
    "qew": "Qualified Electrical Worker (QEW)"
};
export const simpleStatusColorCode = {
    "Not Created": "gray",
    "Created": "red", 
    "Saved": "orange",
    "Submitted": "purple", 
    "Re-Submitted": "black", 
    "Approved": "green",
    "Rejected": "gray",
    "Completed": "green"
};
export const simpleStatusColorCodExcelHex = {
    "Created": "FF0000", 
    "Submitted": "800080", 
    "Approved": "00FF00",
    "Rejected": "808080"
};
export const locatingApproval = [
    "mireya@besstestlab.com",
    "neyda@besstestlab.com"
];
export const scanningApproval = [
    "jeverly@besstestlab.com",
];
export const OfficesLocationMap:{
    [key: string]: LatLngTuple 
} = {
    "Hayward Office": [37.61498, -122.08525], // 2463 Tripaldi Way, Hayward, CA 94545
    "Sacramento Office": [38.52465, -121.39588], // 18 Business Park Way, Sacramento, CA 95828
    "Fresno Office": [36.79860, -119.85823], // 4284 N Knoll Ave., Fresno, CA 93722
    "Ontario Office": [34.041040, -117.619390], // 1508 E Francis St, Ontario, CA 91761
    "Fremont Yard": [37.496590, -121.929090] // 801 Boggs Ave., Fremont, CA 94539
}
export const isMobilePhone = (navigator as json).userAgentData?.mobile ?? ((navigator.userAgent.toLowerCase().search("mac") > -1) || (navigator.userAgent.toLowerCase().search("iphone") > -1) || (navigator.userAgent.toLowerCase().search("android") > -1));
export const localWeekDays = [...weekDays.slice(4), ...weekDays.slice(0, 4)];
export const validateEmail = (email:string) => {
    return email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };
// Class -----------------------------------------------------
export class MyDate extends Date{
    s(){
        return this.toLocaleDateString("fr-CA", {timeZone: "America/Los_Angeles"});
    }
    ss(){
        return this.toLocaleTimeString("En-US", {timeZone: "America/Los_Angeles"});
    }
};
export class MyFormData extends FormData {
    constructor(args?: HTMLFormElement){
        super(args);
        for(let pair of this.entries()){
            if(typeof(pair[1]) === "string"){
                this.set(pair[0], pair[1].trim());
            }
        }
    }
};
// Functions -----------------------------------------------------
// one-liner
export const calculateDistance = (lat: number, lng: number, center: number[]) => (Math.sqrt(Math.pow((lat - center[0]) * 69, 2) + Math.pow((lng - center[1]) * 54.6, 2)));
export const payRollRuleChangeDate = (date: Date) => (new Date(date.getFullYear() + "/3/31")) < date && date < (new Date(date.getFullYear() + "/11/15"));
export const round = (e: string | number, i: number = 0) => window.Number.parseFloat((typeof(e) === "string"?window.Number.parseFloat(e):e).toFixed(i));
export const onlyUnique = (value: simple, index: number, self: simple[]) => (value !== 0 && !value)?false:(self.indexOf(value) === index);
export const ascSort = (a: simple, b: simple) => a! > b!? 1 : -1;
export const descSort = (a: simple, b: simple) => a! < b!? 1 : -1;
export const objAscSort = (a: json, b: json, sortParameter: string, options?: {caseInsensitive: boolean}) => options?.caseInsensitive?(a[sortParameter]?.toLowerCase() > b[sortParameter]?.toLowerCase()? 1 : -1):(a[sortParameter] > b[sortParameter]? 1 : -1);
export const objDescSort = (a: json, b: json, sortParameter: string, options?: {caseInsensitive: boolean}) => options?.caseInsensitive?(a[sortParameter]?.toLowerCase() < b[sortParameter]?.toLowerCase()? 1 : -1):(a[sortParameter] < b[sortParameter]? 1 : -1);
export const boolOrReducer = (accumulator: boolean, currentValue: boolean) => accumulator || currentValue;
export const boolAndReducer = (accumulator: boolean, currentValue: boolean) => accumulator && currentValue;
export const numberReducer = (accumulator: number, currentValue: number) => accumulator + currentValue;
export const arrayReducer = (accumulator: any[], currentValue: any[]) => [...accumulator, ...currentValue];
export const objectReducer = (accumulator: json, currentValue: json) => ({...accumulator, ...currentValue});
export const toProperCase = (str: string) => str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
// normal
export const test = (e:any) => {
    if(isDevMode) console.log(e);
    return e;
}
export const convertNumbersToLetters = (number:number) => {
    const dict = "ZABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let n = number;
    let s = "";
    while(n > 26){
      const m = n % 26;
      s = dict[m] + s;
      n = Math.floor(n / 26);
      if(m === 0) n--;
    }
    return dict[n] + s;
}

export const convert24 = (str: string) => {
    if(!str) return str;
    // 06:05:06
    const timeArr = str.split(":");
    const h = window.parseInt(timeArr[0]);
    const m =timeArr[1];
    const s = timeArr[2];
    if(h < 12) return `${h}:${m}:${s} AM`;
    if(h === 12) return `12:${m}:${s} PM`;
    const realH = h - 12;
    return `${realH}:${m}:${s} PM`;
}
export const isDstObserved = (date: Date) => { // unused
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    const stdTimezoneOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    return date.getTimezoneOffset() < stdTimezoneOffset;
}
export const addDate = (dateString: string, num: number) => {
    if(
        !dateString ||
        dateString === defaultEmptyDate || 
        dateString === defaultEmptyDate.replace(/-/g,"/")
    ) return "";
    const date = new MyDate(dateString.replace(/-/g,"/"));
    date.setDate(date.getDate() + num);
    return date.s();
}
export const usaExpireColorCode = (e: string) => {
    if(e === "") return "";
    const date = new Date(e);
    if(date < new Date()) return "red";
    date.setDate(date.getDate() - 14);
    if(date < new Date()) return "yellow";
    return "rgba(4,170,109,0.8)";
    
}
export const showSize = (number: number) => {
    let n = number;
    if(n < 1024){return n;}
    n = round(String(n / 1024), 2);
    if(n < 1024){return n + " K";}
    n = round(String(n / 1024), 2);
    if(n < 1024){return n + "M";}
    n = round(String(n / 1024), 2);
    if(n < 1024){return n + "G";}
}
// const token = useAuthStore(state => state.token);
//  const setToken = useAuthStore(state => state.setToken);
export const myAxios = async(
{
    fileName, 
    formData, 
    responseType, 
    signal,
    redirectPath
}:
{
    fileName: string, 
    formData?: FormData, 
    responseType?: ResponseType, 
    signal?: GenericAbortSignal,
    redirectPath?: string,
}
) => {
    const api = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        timeout: 30000,
    })
    api.interceptors.request.use(
        config => {
            if (!navigator.onLine) {
                return Promise.reject(new Error("OFFLINE"));
            }
            // 如果你以后要加 token header
            // const token = useAuthStore.getState().token;
            // if (token) config.headers.Authorization = token;
            return config;
        },
        error => Promise.reject(error)
    );
    api.interceptors.response.use(
        res => res,
        async err => {
            if (axios.isCancel(err) || err.message === "canceled") {
                return Promise.reject({ silent: true });
            }
            if (err.response) { 
                const contentType = err.response.headers["content-type"] ?? "";
                if (contentType.includes("application/json") && err.response.data instanceof Blob) {
                    try {
                        const text = await err.response.data.text();
                        err.response.data = JSON.parse(text);
                    } catch {
                        // leave data as-is if parsing fails
                    }
                }

                const status = err.response.status;
                if((status !== 401 && status !== 403) || (status === 404 && !redirectPath)){
                    console.log(err);
                    useModalStore.getState().showModal({
                        title: err.response.data.title ?? "Error",  
                        message: err.response.data.msg ?? (status === 500? "Internal Error" : ""),
                    });
                }
                if (status === 401 || status === 403) {
                    const from = window.location.pathname + window.location.search;
                    if(useAuthStore.getState().isAuthenticated){
                        useModalStore.getState().showModal({
                            title: err.response.data.title ?? "Error",  
                            message: err.response.data.msg ?? (status === 500? "Internal Error" : ""),
                            onConfirm: () => { 
                                setFromOnce(from);
                                window.location.href = "/Login";
                            }
                        });
                    }else{
                        setFromOnce(from);
                        window.location.href = "/Login";
                    }
                }
                if(status === 404 && redirectPath){
                    useModalStore.getState().showModal({
                        title: err.response.data.title ?? "Error",  
                        message: err.response.data.msg ?? (status === 500? "Internal Error" : ""),
                        onConfirm: () => {throw redirect(redirectPath);}
                    });
                }
                return false;
            }
            return Promise.reject(new Error("NETWORK_ERROR"));
        }
    );
    const data = formData ?? new FormData();
    return api.post(fileName, data, {
        responseType,
        signal,
    });
}
const FROM_KEY = "auth:from";
export function setFromOnce(from: string) {
  if (!sessionStorage.getItem(FROM_KEY)) {
    sessionStorage.setItem(FROM_KEY, from);
  }
}
export function consumeFrom(): string {
  const from = sessionStorage.getItem(FROM_KEY) ?? "/";
  sessionStorage.removeItem(FROM_KEY);
  return from;
}
export const post_max_size = 8 * 1024 * 1024; // PHP_INI_PERDIR
export const handleFilesInput = (e: SyntheticEvent) => {
    const event = e.nativeEvent;
    const target = event.target as HTMLInputElement;
    const files = target.files!;
    if(files.length === 0) return;
    const nextElementSibling = target.nextElementSibling! as HTMLLabelElement;
    let size = 0;
    if(files.length === 1){
        size = files[0].size;
        if(files[0].size < 100 * 1024){
            if(!window.confirm(`The file size (${(files[0].size / 1024).toFixed(2)}k) is too small (less than 100k). Are you sure to upload?\n(Images can be selected as Medium or Large for better quality.)`)){
                target.value = "";
                nextElementSibling.innerText = "Choose file(s)";
                return;
            }
        }
    }else if(files.length > 1){
        for(const file of files){
            size += file.size;
            if(file.size < 100 * 1024){
                if(!(window.confirm(`One of the file size (${(file.size / 1024).toFixed(2)}k) is too small (less than 100k). Are you sure to upload?\n(Images can be selected as Medium or Large for better quality.)`)!)){
                    target.value = "";
                    nextElementSibling.innerText = "Choose file(s)";
                    return;
                }
            }
        }
    }
    if(size > post_max_size){
        const upload_size_in_M = round(size / 1024 / 1024, 2);
        const post_max_size_in_M = round(post_max_size / 1024 / 1024);
        if(!window.confirm(`Upload size (${upload_size_in_M}M) exceed max allowable size ${post_max_size_in_M}M. Still Proceed? (This may cause error.)`)){
            target.value = "";
            nextElementSibling.innerText = "Choose file(s)";
            return;
        }
    }
    if(files.length === 1){
        nextElementSibling.innerText = files[0].name;
    }else if(files.length === 0){
        nextElementSibling.innerText = "Choose file(s)";
    }else{
        nextElementSibling.innerText = `${files.length} file(s)`;
    }
}
export const scroll = (id?: string | null) => {
    if(id === undefined){
        return;
    }else if(id === null){
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth"
        })
    }else{
        const interval = setInterval(() => {
            if(document.getElementById(id)){
                window.setTimeout(() => {
                    document.getElementById(id)?.scrollIntoView({
                        behavior: "smooth", block: "start"
                    });
                }, 200);
            }
        }, 200);
        window.setTimeout(() => {
            clearInterval(interval);
        }, 400);
    }
}
export const getJsonArray = (e: string) => {
    try{
        return JSON.parse(e);
    }catch(error){
        window.alert("System error occurred on your files.");
        return [];
    }
}
export const statusColorCode = (status: string, workStatus?: string) => {
    switch(status){
        case "Created":
            return "red";
        case "Saved":
            return "purple";
        case "Submitted":
            if(workStatus !== undefined && workStatus !== "Work"){
                return "gray"
            }else{
                return "green";
            }
        case "Rejected":
            return "blue";
        default:
            return "black";
    }
}
export const weeknum = (date_string: Date | string) => {
    if(!date_string) return "";
    let res: string;
    if(date_string instanceof MyDate){
        res = date_string.s().replace(/-/g, "/");
    }else if(date_string instanceof Date){
        res = date_string.toLocaleDateString("fr-CA", {timeZone: "America/Los_Angeles"}).replace(/-/g, "/");
    }else{
        res = date_string;
    }
    const target = new Date(res.substring(0, 10).replace(/-/g, "/"));
    const dayNr = (target.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
        target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    const number = 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
    const year = target.getFullYear();
    const result = "w" + year + "-" + (number < 10? "0" + number.toString(): number.toString());
    return result;
}
// Components -----------------------------------------------------
export const defaultPath = "/opt/bitnami/apache/htdocs";
export const weekIndexes = [1, 2, 3, 4, 5, 6, 0];
export const plannedTimeExcel = (start:string, end:string, allDay:boolean) => {
    if(start.substring(0, 10) === end.substring(0, 10)){
        //same Day
        if(allDay){
            return {"richText": [
                {"font": {"bold": true},"text": start.substring(0, 10)}
            ]};
        }else{
            return {"richText": [
                {"font": {"bold": true},"text": start.substring(0, 10)},
                {"text": start.substring(10, 16) + " ~ " + end.substring(11, 16)}
            ]};
        }
    }else{
        //overnight
        if(allDay){
            return {"richText": [
                {"font": {"bold": true},"text": start.substring(0, 10)},
                {"text": " ~ "},
                {"font": {"bold": true},"text": end.substring(0, 10)},
            ]};
        }else{
            return {"richText": [
                {"font": {"bold": true},"text": start.substring(0, 16)},
                {"text": " ~ "},
                {"font": {"bold": true},"text": end.substring(0, 16)},
            ]};
        }
    }
}
export const workLogSubmitTimeExcel = (e:string) => {
    return {"richText": [
        {"font": {"bold": true},"text": e.split(" ")[0]},
        {"text": " " + e.split(" ")[1]}
    ]};
}

export const validURL = (str:string) => {
    const pattern = new RegExp("^(https?:\\/\\/)?"+ // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|"+ // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))"+ // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*"+ // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?"+ // query string
        "(\\#[-a-z\\d_]*)?$","i"); // fragment locator
    return !!pattern.test(str);
}

export const GeotabApiOptions = {
    rememberMe: true, 
    timeout: 10,
    fullResponse: true
}
export const GeotabApiAuthentication = {
    credentials: {
        database: 'besstestlab',
        userName: 'jun@besstestlab.com',
        password: 'perfect909'
    },
    path: 'my.geotab.com'
}

export const getFileNameFromUrl = (fileUrl:string) => {
    const url = new URL(fileUrl);
    const params = new URLSearchParams(url.search);
    const contentDisposition = params.get("response-content-disposition");
    if(contentDisposition){
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^"]+)"?/);
        return match ? decodeURIComponent(match[1]) : null;
    }
  
    return url.pathname.split("/").pop();
};

export async function convertFilesFromHEICtoJPEG(files:File[]){
    return  await Promise.all(
        files.map(async (file) => {
        if (file.type === "image/heic") {
            let blob = await heic2any({ blob: file, toType: "image/jpeg" });
            if (Array.isArray(blob)) blob = blob[0];
            file = new File([blob], file.name.replace(/\.heic$/, ".jpg"), {
            type: "image/jpeg",
            });
        }
        return file;
        })
    );
}

export async function compressImage(file: File, purpose: string = "") {
  const options = {
    maxSizeMB: purpose === "profile"? 1: undefined, 
    maxWidthOrHeight: 2000, 
    useWebWorker: true, 
    initialQuality: 0.85, 
    onProgress: (progress:number) => {
        console.log(`compressing: ${progress}%`);
    }
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Compression error:", error);
    return file;
  }
}
export type OS = "mac" | "windows" | "other";
export function detectOS(): OS {
  // 1️⃣ Modern Chromium (no deprecation)
  const uaData = navigator.userAgentData;
  if (uaData?.platform) {
    if (uaData.platform === "macOS") return "mac";
    if (uaData.platform === "Windows") return "windows";
  }
  // 2️⃣ Cross-browser fallback (deprecated but unavoidable)
  const platform = navigator.platform.toLowerCase();
  if (platform.includes("mac")) return "mac";
  if (platform.includes("win")) return "windows";
  return "other";
}

export function getCurrentWeekRange() {
    const today = new Date();
    const day = today.getDay(); // 0 = Sun, 1 = Mon
    // Calculate Monday
    const monday = new Date(today);
    monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
    // Calculate Sunday
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    return {
    start: fmt(monday),
    end: fmt(sunday)
    };
}

export const taskStatusColorCode = {
    "Created": "red", 
    "In Progress": "purple", 
    "Complete": "green"
};
export const taskStatus = [
    "Created", 
    "In Progress", 
    "Complete"
];

export const getISOYearWeek = (): string => {
    const now = new Date();
    const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    const year = date.getUTCFullYear();
    return `${year}${String(week).padStart(2, "0")}`;
};